import React from "react";
import { Link } from "@tanstack/react-router";
import {
  Pagination,
  PaginationContent,
  PaginationEllipsis,
  PaginationItem,
  PaginationLink,
  PaginationNext,
  PaginationPrevious,
} from "@/components/ui/pagination";

interface BlogPaginationProps {
  currentPage: number;
  totalPages: number;
  hasNextPage: boolean;
  hasPrevPage: boolean;
}

export function BlogPagination({
  currentPage,
  totalPages,
  hasNextPage,
  hasPrevPage,
}: BlogPaginationProps) {
  // Don't render pagination if there's only one page or no pages
  if (totalPages <= 1) {
    return null;
  }

  // Generate page numbers to display
  const getVisiblePages = () => {
    const delta = 2; // Number of pages to show on each side of current page
    const range = [];
    const rangeWithDots = [];

    for (
      let i = Math.max(2, currentPage - delta);
      i <= Math.min(totalPages - 1, currentPage + delta);
      i++
    ) {
      range.push(i);
    }

    if (currentPage - delta > 2) {
      rangeWithDots.push(1, "...");
    } else {
      rangeWithDots.push(1);
    }

    rangeWithDots.push(...range);

    if (currentPage + delta < totalPages - 1) {
      rangeWithDots.push("...", totalPages);
    } else {
      if (totalPages > 1) {
        rangeWithDots.push(totalPages);
      }
    }

    return rangeWithDots;
  };

  const visiblePages = getVisiblePages();

  // Helper function to generate the correct URL for a page
  const getPageUrl = (pageNumber: number) => {
    if (pageNumber === 1) {
      return "/blog";
    }
    return `/blog/page/${pageNumber}`;
  };

  return (
    <div className="mt-12">
      <Pagination>
        <PaginationContent>
          {hasPrevPage && (
            <PaginationItem>
              <Link to={getPageUrl(currentPage - 1)}>
                <PaginationPrevious />
              </Link>
            </PaginationItem>
          )}

          {visiblePages.map((page, index) =>
            page === "..." ? (
              <PaginationItem key={`ellipsis-before-${visiblePages[index + 1] || "end"}`}>
                <PaginationEllipsis />
              </PaginationItem>
            ) : (
              <PaginationItem key={`page-${page}`}>
                <Link to={getPageUrl(page as number)}>
                  <PaginationLink isActive={currentPage === page}>{page}</PaginationLink>
                </Link>
              </PaginationItem>
            ),
          )}

          {hasNextPage && (
            <PaginationItem>
              <Link to={getPageUrl(currentPage + 1)}>
                <PaginationNext />
              </Link>
            </PaginationItem>
          )}
        </PaginationContent>
      </Pagination>
    </div>
  );
}
